import PropTypes from "prop-types";
import React, { useEffect, useMemo, useRef, useState } from "react";
import Heading from "../../elements/heading";
import Input from "../../elements/input";
import PrimaryButton from "../../elements/primary-button";
import SimpleLink from "../../elements/simple-link";
import BodyText from "../../elements/body-text";
import { getSMSCode } from "../../services/verificationService";
import "./style.scss";

const VerifyPhoneNumberForm = ({
  formTitleType,
  formTitleBody,
  body,
  verificationCodeLabel,
  verificationCodeInfoText,
  verificationCodeWarningMessage,
  verificationCodeMissingMessage,
  verifyCodeFailedMessage,
  verifyPhoneNumberFailedMessage,
  mobileInUseMessage,
  resendCodeButtonText,
  resendSuccessMessage,
  resendReachMaxErrorMessage,
  resendErrorMessage,
  ctaName,
  colorScheme,
  iconName,
  disclaimerText,
  phoneAreaEntered,
  phoneNumberEntered,
  sendCodeFailed,
  verifyCodeFailed,
  submitFailed,
  mobileInUse,
  codeType,
  onVerificationCodeSubmitted,
  isReachedMax
}) => {
  const submitVerificationCodeFunc = (evt, data) => {
      onVerificationCodeSubmitted(evt, data);
    },
    //TODO update aoi to phone verification
    resendVerificationCodeFunc = async () => {
      try {
        await getSMSCode(phoneAreaEntered, phoneNumberEntered, codeType);
        setResendSuccessful(true);
      } catch (ex) {
        if (ex.code === "USER_CUSTOMER_0013") {
          setReachedMax(true);
        } else {
          setResendSuccessful(false);
        }
      }
    };

  const resendButtonRef = useRef();
  const resendMessageRef = useRef();

  const [formDataVerificationCode, setVerificationCode] = useState(null);
  const [showResendButton, setShowResendButton] = useState(true);

  const [reachedMax, setReachedMax] = useState(isReachedMax);
  const [resendSuccessful, setResendSuccessful] = useState(true);
  const [verificationCodeMissing, setVerificationCodeMissing] = useState(false);
  const codeWarningMessage = useMemo(() => {
    return verificationCodeMissing ? verificationCodeMissingMessage.value : sendCodeFailed ? verificationCodeWarningMessage.value : verifyCodeFailed ? verifyCodeFailedMessage.value : null;
  }, [sendCodeFailed, verificationCodeMissing, verifyCodeFailed]);
  useEffect(() => {
    if (resendButtonRef.current && resendMessageRef.current) {
      if (showResendButton) {
        resendButtonRef.current.styles.display = "inline-block";
        resendMessageRef.current.styles.display = "none";
      } else {
        resendMessageRef.current.styles.display = "inline-block";
        resendButtonRef.current.styles.display = "none";
      }
    }
  }, [showResendButton]);
  const onVerificationCodeFocus = evt => {
    setVerificationCodeMissing(false);
  };
  const onVerificationCodeChange = evt => {
    setVerificationCode(evt.target.value);
  };

  const onSubmit = evt => {
    let isValid = true;
    if (!formDataVerificationCode) {
      isValid = false;
      setVerificationCodeMissing(true);
    } else {
      setVerificationCodeMissing(false);
    }
    if (isValid) {
      let data = { code: formDataVerificationCode.trim() };
      submitVerificationCodeFunc(evt, data);
      evt.preventDefault();
    }
  };

  const onResendCodeClick = evt => {
    // hide resend button for 5 sec
    setShowResendButton(false);
    setTimeout(() => {
      setShowResendButton(true);
    }, 5000);
    resendVerificationCodeFunc(evt);
  };

  const getResendMessage = () => {
    if (reachedMax) {
      return <span className="error">{resendReachMaxErrorMessage.value}</span>;
    } else if (!resendSuccessful) {
      return <span className="error"> {resendErrorMessage.value}</span>;
    } else {
      return <span> {resendSuccessMessage.value}</span>;
    }
  };

  return (
    <div className={"verify-phone-number-form"}>
      {formTitleBody && (
        <Heading
          type={formTitleType}
          className="title show-on-scroll"
          html={{
            value: `<span>${formTitleBody.value.trim()} ${phoneAreaEntered} ${phoneNumberEntered}`
          }}
          typographyClass="type-h3"
        />
      )}

      {body && <BodyText html={body.value} />}

      {verificationCodeLabel && (
        <Input
          {...{
            label: verificationCodeLabel.value,
            infoMessage: verificationCodeInfoText.value,
            onChange: evt => onVerificationCodeChange(evt),
            onFocus: evt => onVerificationCodeFocus(evt)
          }}
        />
      )}
      {codeWarningMessage && <BodyText html={codeWarningMessage} className="failed-message" />}
      {submitFailed && verifyPhoneNumberFailedMessage.value && <BodyText html={verifyPhoneNumberFailedMessage.value} className="failed-message" />}
      {mobileInUse && mobileInUseMessage.value && <BodyText html={mobileInUseMessage.value} className="failed-message" />}
      <div className="button-container">
        {ctaName && ctaName.value && (
          <PrimaryButton
            {...{
              colorScheme: colorScheme,
              iconName: iconName,
              link: {
                value: {
                  text: ctaName.value
                }
              },
              callback: evt => onSubmit(evt)
            }}
          />
        )}

        {resendCodeButtonText && resendCodeButtonText.value && showResendButton && (
          <SimpleLink
            {...{
              text: resendCodeButtonText.value,
              callback: evt => onResendCodeClick(evt)
            }}
            ref={resendButtonRef}
          />
        )}
        {!showResendButton && (
          <div className="resend-code-confirmation" ref={resendMessageRef}>
            {getResendMessage()}
          </div>
        )}
      </div>

      {disclaimerText && disclaimerText.value !== "" && (
        <div className="change-email-modal-disclaimer">
          <BodyText html={disclaimerText.value} className="disclaimer" />
        </div>
      )}
    </div>
  );
};

VerifyPhoneNumberForm.propTypes = {
  formTitleType: PropTypes.object,
  formTitleBody: PropTypes.object,
  body: PropTypes.shape({ value: PropTypes.string }),
  verificationCodeLabel: PropTypes.shape({ value: PropTypes.string }),
  verificationCodeInfoText: PropTypes.shape({ value: PropTypes.string }),
  verificationCodeWarningMessage: PropTypes.shape({ value: PropTypes.string }),
  verifyEmailFailedMessage: PropTypes.shape({ value: PropTypes.string }),
  resendCodeButtonText: PropTypes.shape({ value: PropTypes.string }),
  resendSuccessMessage: PropTypes.shape({ value: PropTypes.string }),
  resendReachMaxErrorMessage: PropTypes.shape({ value: PropTypes.string }),
  resendErrorMessage: PropTypes.shape({ value: PropTypes.string }),
  ctaName: PropTypes.shape({ value: PropTypes.string }),
  colorScheme: PropTypes.object,
  iconName: PropTypes.object,
  disclaimerText: PropTypes.shape({ value: PropTypes.string }),
  submittedPhoneAreaCode: PropTypes.string,
  submittedPhoneNumber: PropTypes.string
};

export default VerifyPhoneNumberForm;
